// Conditionally load DatadogDebugHelper only in non-production environments
// This prevents it from being bundled in production and polluting the browser console
if (process.env.NODE_ENV !== 'production') {
  void import('../utils/DatadogDebugHelper');
}

import { datadogRum } from '@datadog/browser-rum';
import { ThemeProvider } from '@mui/material/styles';
import { get as _get } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import { getClaims } from '../api/getClaimsApi';
import { getPolicy } from '../api/getPolicyApi';
import { getQueryString } from '../api/urlResolver';
import { validateSession } from '../api/validateContextRouterApi';
import ClaimsOverview from '../components/fnol/claims-overview/ClaimsOverview';
import ConfirmInsureds from '../components/fnol/confirm-insured/ConfirmInsured';
import DriverSelection from '../components/fnol/driver-selection/DriverSelection';
import DynamicQuestion from '../components/fnol/dynamic-question/DynamicQuestion';
import ERSCallUs from '../components/fnol/ers-call-us/ERSCallUs';
import ExistingClaims from '../components/fnol/existing-claims/ExistingClaims';
import GlassCallUs from '../components/fnol/glass-call-us/GlassCallUs';
import ReporterInfo from '../components/fnol/reporter-info/ReporterInfo';
import RiskSelection from '../components/fnol/risk-selection/RiskSelection';
import TimeLocationSelection from '../components/fnol/time-location-selection/TimeLocationSelection';
import AutoConfirmation from '../components/post-fnol-auto/AutoConfirmation/AutoConfirmation';
import AutoServiceConfirmation from '../components/post-fnol-auto/AutoConfirmation/AutoServiceConfirmation';
import PhotoServicesConfirmation from '../components/post-fnol-auto/PhotoServices/PhotoServicesConfirmation';
import PhotoServicesHome from '../components/post-fnol-auto/PhotoServices/PhotoServicesHome';
import PhotoServicesSelection from '../components/post-fnol-auto/PhotoServices/PhotoServicesSelection';
import RentalServicesFinalize from '../components/post-fnol-auto/RentalServices/RentalServicesFinalize';
import RentalServicesHome from '../components/post-fnol-auto/RentalServices/RentalServicesHome';
import RentalServiceSelection from '../components/post-fnol-auto/RentalServices/RentalServicesSelection';
import RepairServicesHome from '../components/post-fnol-auto/RepairServices/RepairServicesHome';
import RepairServiceSelection from '../components/post-fnol-auto/RepairServices/RepairServicesSelection';
import DocumentDamageServiceSelection from '../components/post-fnol-property/DocumentDamageServices/DocumentDamageServiceSelection';
import DocumentDamageServicesHome from '../components/post-fnol-property/DocumentDamageServices/DocumentDamageServicesHome';
import EmergencyServicesHome from '../components/post-fnol-property/EmergencyServices/EmergencyServicesHome';
import PropertyConfirmation from '../components/post-fnol-property/PropertyConfirmation/PropertyConfirmation';
import ServicesSelection from '../components/post-fnol-property/ServicesSelection/ServicesSelection';
import WaterMitigationServiceHome from '../components/post-fnol-property/WaterMitigation/WaterMitigationServiceHome';
import Accordion from '../components/shared/Accordion';
import AdjusterContact from '../components/shared/AdjusterContact/AdjusterContact';
import ClaimDetailDetails from '../components/shared/ClaimDetail/ClaimDetailDetails';
import ErrorBanner from '../components/shared/ErrorBanner/ErrorBanner';
import FinalServicesConfirmation from '../components/shared/FinalServicesConfirmation/FinalServicesConfirmation';
import ModalLinearActivityIndicator from '../components/shared/ModalLinearActivityIndicator/ModalLinearActivityIndicator';
import NoValidPoliciesFound from '../components/shared/NoValidPoliciesFound/NoValidPoliciesFound';
import SingleServiceConfirmation from '../components/shared/ServicesConfirmation/SingleServiceConfirmation';
import TimeoutConfirmation from '../components/shared/Timeout/TimeoutConfirmation';
import Vehicles from '../components/shared/Vehicles/Vehicles';
// Use height-stabilized scroll wrapper to fix intermittent review screen cutoff issue.
// Waits for layout animations to settle before sending scroll-to-top message to MyAccount.
// Reference: OPTION_B_IMPLEMENTATION.md, review-screen-top-cutoff-fixed-header-overlap.md
import { ScrollMessageWrapperHeightStabilized as ScrollMessageWrapper } from '../components/shared/Wrappers/ScrollMessageWrapperHeightStabilized';
import ThirdPartyConfirmation from '../components/third-party-fnol/ConfirmationPage/ThirdPartyConfirmation';
import DuplicateClaimConfirmation from '../components/third-party-fnol/ErrorPages/DuplicateClaim';
import PolicyNotFoundConfirmation from '../components/third-party-fnol/ErrorPages/PolicyNotFound';
import { CallingSubcontexts } from '../Constants/Constants';
import { PostMessageCodes } from '../Constants/PostMessageCodes';
import { initiateOptimizelyFeatureFlagsCall } from '../services/SetupOptimizely.service';
import { setupPostFnol } from '../services/SetupPostFnol.service';
import { afiChameleonFinalTheme } from '../themes/afiChameleonFinalTheme';
import { loadThemeByOpCoId } from '../themes/themeMapper';
import { GenericProductType, OptimizelyFlags } from '../types/type';
import { hasClaimsRetrievalIssue } from '../utils/ClaimsResponse-Util';
import { DatadogErrorTracker } from '../utils/DatadogErrorTracker';
import { DatadogResolver } from '../utils/DatadogResolver';
import { isPolicyInForce, mapDetailedPolicies, mapE1PPolicies, mapPolicyType } from '../utils/Policy-Util';
import { getClaimType, mapPartnerCodeToBrandOpcoId, postMessageToParentApp } from '../utils/Utilities';
import { useAppDispatch } from './hooks/hooks';
import { addFnolReporter } from './store/ClaimCreation/ClaimCreationSlice';
import { setClaimsRetrievalIssue, updateClaimsState } from './store/Claims/ClaimsSlice';
import * as ContextRouterActions from './store/ContextRouter/ContextRouterActions';
import { useRouter } from './store/GlobalAppData/ReactContextRouter';
import { updateFeatureFlags } from './store/OptimizelyFlags/OptimizelyFlagsSlice';
import { loadAllNewPolicies, loadNewPolicy } from './store/Policies/PoliciesSlice';
import { store } from './store/store';
import { useActions } from './store/useActions';

function App() {
  // State variables
  const [derivedOpcoId, setDerivedOpcoId] = useState('');
  const [modalLoadingOpen, setModalLoadingOpen] = useState(false);
  const [claimNumber, setClaimNumber] = useState('');
  const [getFeatureFlagData, setGetFeatureFlagData] = useState<OptimizelyFlags | null>(null);
  const [sessionInitialized, setSessionInitialized] = useState(false);

  // Hooks and utilities
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const contextRouterActions = useActions(ContextRouterActions);
  const { setGlobalAppState } = useRouter();

  let bodyContent: JSX.Element = <div></div>;

  useEffect(() => {
    // Environment setup
    const environment = process.env['NX_DCV2_UI_ENVIRONMENT'] || '';
    const version = process.env['NX_DCV2_UI_APP_VERSION'] || '';
    const build = process.env['NX_DCV2_UI_APP_BUILD_NUMBER'] || '';

    /**
     *
     * For more information about Datadog integration, see here:
     * https://amfament.atlassian.net/wiki/spaces/AV/pages/16407825279/DataDog+-+React+SPA+application+RUM+app+.+-+Only+for+Browser
     *
     **/
    console.log(`DigitalClaimsV2-UI -  Env: ${environment} Version: ${version} Build: ${build}`);
    datadogRum.init(DatadogResolver.resolveConfig(environment, version));

    // Option 2 & 6: Set early available data immediately after init
    const queryParams = new URLSearchParams(window.location.search);
    const urlKey = queryParams.get('urlKey');
    if (urlKey) {
      datadogRum.setGlobalContextProperty('urlId', urlKey);
    }
    // Option 6: Track session lifecycle status
    datadogRum.setGlobalContextProperty('sessionStatus', 'initializing');
  }, []);

  // Memoize trusted origins to prevent unnecessary re-renders
  const trustedOrigins = useMemo(() => [process.env['NX_BRIDGE_APP_URL']], []);

  // Initialize session and load feature flags
  useEffect(() => {
    if (sessionInitialized) return;

    const initiateSession = async () => {
      const queryParams = await getQueryString();
      if (!queryParams) return;

      setSessionInitialized(true);
      contextRouterActions.validate(queryParams.urlKey, queryParams.custKey);

      const response = await validateSession(queryParams.urlKey, queryParams.custKey);

      const sessionId = _get(response, 'data.afeSessionId', uuid());
      console.log('sessionId:', sessionId);

      // Load feature flags
      const featureFlagData = await initiateOptimizelyFeatureFlagsCall(
        queryParams.urlKey,
        sessionId,
        _get(response, 'data.updatedAuthToken', '')
      );
      console.log('Feature flags loaded:', featureFlagData);
      store.dispatch(updateFeatureFlags({ OptimizelyResponse: featureFlagData }));
      setGetFeatureFlagData(featureFlagData);

      if (_get(response, 'status.statusCode', '') >= 200) {
        contextRouterActions.validateSuccess(response.data);

        // Add reporter info
        dispatch(
          addFnolReporter({
            reporterInfo: {
              contactReferenceKey: '1',
              firstName: _get(response, 'data.customerInfo.firstName'),
              lastName: _get(response, 'data.customerInfo.lastName'),
              phoneNumber: _get(response, 'data.customerInfo.phoneNumber'),
              emailAddress: _get(response, 'data.customerInfo.emailAddress'),
              relationshipToInsured: 'Self',
            },
          })
        );
        postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_APP_IS_ACTIVE);
        await routeAfterValidate(queryParams.urlKey, sessionId, response.data);
      } else {
        handleValidationError(response);
      }
    };

    initiateSession();
  }, [sessionInitialized]);

  useEffect(() => {
    const messageHandler = async (event: MessageEvent) => {
      if (trustedOrigins.includes(event.origin)) {
        const code = _get(event, 'data.messageName', 'NO_MESSAGE_FOUND');
        switch (code) {
          case PostMessageCodes.BRIDGE_APP_HOSTED_STATE:
            setGlobalAppState({ hostedByBridgeApp: true });
            break;
        }
      }
    };
    window.addEventListener('message', messageHandler, false);
    return () => {
      window.removeEventListener('message', messageHandler);
    };
  }, [setGlobalAppState, trustedOrigins]);

  const handleValidationError = (response: any) => {
    // Option 6: Track validation failure in session status
    datadogRum.setGlobalContextProperty('sessionStatus', 'validation-failed');
    contextRouterActions.validateFail('Validate Failed');
    postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_USER_SESSION_VALIDATE_FAIL_ERROR, '', response);
    navigate('/error-banner', { state: { claimNumber } });
  };

  const routeAfterValidate = async (urlKey: string, sessionId: string, customerInfo: any) => {
    const opCo = _get(customerInfo, 'distributionChannel.opco', '');
    const partnerId = _get(customerInfo, 'distributionChannel.partnerId', '');

    // Set theme and opco info
    contextRouterActions.setThemeOpCo(opCo);

    /*
    In a basic scenario, each operating company (opco) is associated with a single specific theme and a unique set of questions,
    for example, CAH, MSA, AFI, etc. However, an operating company can also collaborate with several partners, encompass multiple themes,
    and utilize various sets of questions, such as with the Polaris platform.
    This function sifts through the data to present a streamlined view of the operating company identifier.
    */
    const derivedOpCoId = mapPartnerCodeToBrandOpcoId(opCo, partnerId);
    setDerivedOpcoId(derivedOpCoId);
    setModalLoadingOpen(true);

    // Option 1: Set user identity for better attribution and filtering
    datadogRum.setUser({
      id: sessionId,
      email: _get(customerInfo, 'customerInfo.emailAddress'),
    });

    // Set all global context properties
    const policies = customerInfo?.claimsInfo?.policies?.reduce((accumulator: string[], policy: any) => {
      accumulator.push(policy.policyNumber);
      return accumulator;
    }, []);
    datadogRum.setGlobalContextProperty('policyNumbers', policies); // Adding this allows us to filter sessions based upon @context.policyNumbers value in DataDog Session Explorer
    datadogRum.setGlobalContextProperty('urlId', urlKey); // Adding this allows us to filter sessions based upon @context.urlId value in DataDog Session Explorer
    datadogRum.setGlobalContextProperty('afe-session-id', sessionId); // Adding this allows us to filter sessions based upon @context.afe-session-id value in DataDog Session Explorer
    datadogRum.setGlobalContextProperty('derivedOpCoId', derivedOpCoId); // Adding this allows us to filter sessions based upon @context.derivedOpCoId value in DataDog Session Explorer
    datadogRum.setGlobalContextProperty('opCo', opCo); // Adding this allows us to filter sessions based upon @context.opCo value in DataDog Session Explorer
    datadogRum.setGlobalContextProperty('partnerId', partnerId); // Adding this allows us to filter sessions based upon @context.partnerId value in DataDog Session Explorer
    datadogRum.setGlobalContextProperty('roleType', _get(customerInfo, 'customerInfo.roleType', '')); // Adding role type for additional filtering
    // Option 6: Update session status to validated
    datadogRum.setGlobalContextProperty('sessionStatus', 'validated');

    // Load policies
    await loadPolicies(customerInfo, urlKey, sessionId);

    // Route based on user type and context
    await handleRouting(customerInfo, urlKey, sessionId);
  };

  const loadPolicies = async (customerInfo: any, urlKey: string, sessionId: string) => {
    const detailedPolicies = _get(customerInfo, 'claimsInfo.detailedPoliciesInfo', []);
    const policies = _get(customerInfo, 'claimsInfo.policies', []);

    if (detailedPolicies.length > 0) {
      const mappedPolicies = mapDetailedPolicies(
        detailedPolicies,
        _get(customerInfo, 'distributionChannel.opco', ''),
        _get(customerInfo, 'distributionChannel.partnerName', ''),
        _get(customerInfo, 'customerInfo.roleType', '')
      );
      dispatch(loadAllNewPolicies({ policies: mappedPolicies }));
    } else if (policies.length > 0 && policies[0].policyNumber) {
      await Promise.all(
        policies.map(async (policy: any) => {
          const policyInfo = await getPolicy(
            urlKey,
            sessionId,
            _get(customerInfo, 'updatedAuthToken', ''),
            policy.policyNumber,
            _get(customerInfo, 'distributionChannel.opco', ''),
            false,
            policy.isE1P
          );

          // Get Product Type
          const productType = getClaimType(_get(policyInfo, 'data.productType', ''));

          const hasValidStatusCode = _get(policyInfo, 'status.statusCode', '') >= 200;

          // Valid Product Types include Auto, Home, Workers Comp, Commercial Auto, and Commercial Property
          const isSupportedProductType =
            productType === GenericProductType.Auto ||
            productType === GenericProductType.Home ||
            productType === GenericProductType.WorkersComp ||
            productType === GenericProductType.CommercialAuto ||
            productType === GenericProductType.CommercialProperty;

          const policyTermStatus = String(_get(policyInfo, 'data.policyTermStatus', ''));
          const isInforcePolicy = isPolicyInForce(policyTermStatus);

          // A policy is loaded only when all three hold:
          //   1. Successful retrieval (status code >= 200)
          //   2. Product type is Auto, Home, Workers Comp, Commercial Auto, or Commercial Property
          //   3. Policy term is inforce (non-inforce policies are excluded)
          //
          // EXCEPTION: Workers Comp policies are always loaded regardless of status
          // because we need them for claim type detection in direct-to-claim-detail flow
          const isValidPolicy = hasValidStatusCode && isSupportedProductType && isInforcePolicy;
          const isWorkersCompPolicy = hasValidStatusCode && productType === GenericProductType.WorkersComp;

          if (isValidPolicy || isWorkersCompPolicy) {
            const mappedPolicy = mapE1PPolicies(
              _get(policyInfo, 'data'),
              _get(customerInfo, 'distributionChannel.opco', ''),
              _get(customerInfo, 'distributionChannel.partnerName', ''),
              _get(customerInfo, 'customerInfo.roleType', '')
            );
            dispatch(loadNewPolicy({ policy: mappedPolicy }));
          } else if (!hasValidStatusCode) {
            // Only report genuine retrieval errors (bad status code); non-inforce and
            // non-Auto/Home policies are silently filtered out
            postMessageToParentApp(
              PostMessageCodes.DIGITAL_CLAIM_V2_POLICY_RETRIEVAL_ERROR,
              policy.policyNumber,
              policyInfo
            );
          }
        })
      );
    }
  };

  const handleRouting = async (customerInfo: any, urlKey: string, sessionId: string) => {
    const roleType = customerInfo.customerInfo.roleType;
    const callingSubContext = customerInfo.callingSubContext;
    const policies = _get(customerInfo, 'claimsInfo.policies', []);

    // E1PCDC-6680: Context Router can return a 200 with no policies on the payload
    // (MyAccount/MyAmFam sent nothing usable). Route to a dedicated "contact us" screen
    // instead of falling through into the FNOL, which expects at least one policy.
    if (policies.length === 0) {
      setModalLoadingOpen(false);
      navigate('/no-valid-policies-found');
      return;
    }

    if (roleType === 'thirdParty') {
      setModalLoadingOpen(false);
      navigate('/third-party-guest-type');
    } else if (callingSubContext === CallingSubcontexts.CLAIMS_OVERVIEW) {
      await initiateClaimsByPolicyCall(customerInfo, urlKey, sessionId);
      navigate('/claims-overview', { state: { isClaimRefreshNeeded: false } });
    } else if (callingSubContext === CallingSubcontexts.RISK_SELECTION) {
      setModalLoadingOpen(false);
      navigate('/risk-selection');
    } else {
      await handlePostFnolSetup(customerInfo, urlKey, sessionId);
    }
  };

  const handlePostFnolSetup = async (customerInfo: any, urlKey: string, sessionId: string) => {
    const fullClaimNumber = _get(customerInfo, 'claimsInfo.fullClaimNumber', '');
    setClaimNumber(fullClaimNumber);
    datadogRum.setGlobalContextProperty('fullClaimNumber', fullClaimNumber);

    // Check if this is a Workers Comp claim by looking at policy type in validate response
    // Workers Comp policies may not be available through product-context API, so check the
    // policy type directly from the validate response first
    const policyType = _get(customerInfo, 'claimsInfo.policies[0].productType', '');
    let generalizedProductType = mapPolicyType(policyType);

    // If we couldn't determine the type from validate response, try Redux
    if (
      generalizedProductType === GenericProductType.Other ||
      generalizedProductType === null ||
      generalizedProductType === undefined
    ) {
      const policyNumber = _get(customerInfo, 'claimsInfo.policies[0].policyNumber', '');

      if (policyNumber) {
        const state = store.getState();
        const allPolicies = _get(state, 'policies.policies', []);
        const loadedPolicy = allPolicies.find((p: any) => p.policyNumber === policyNumber);

        if (loadedPolicy) {
          generalizedProductType = _get(loadedPolicy, 'generalizedProductType', null);
        }
      }
    }

    // If this is a Workers Comp claim, fetch claims data first to populate Redux state
    if (generalizedProductType === GenericProductType.WorkersComp) {
      await initiateClaimsByPolicyCall(customerInfo, urlKey, sessionId);
    }

    const successfulSetup = await setupPostFnol(
      customerInfo,
      fullClaimNumber,
      urlKey,
      sessionId,
      _get(customerInfo, 'updatedAuthToken', ''),
      true,
      _get(customerInfo, 'distributionChannel.opco', ''),
      'bootstrap'
    );

    if (
      successfulSetup === GenericProductType.Auto ||
      successfulSetup === GenericProductType.Home ||
      successfulSetup === GenericProductType.CommercialAuto ||
      successfulSetup === GenericProductType.CommercialProperty ||
      successfulSetup === GenericProductType.WorkersComp
    ) {
      navigateToNextScreen(customerInfo, successfulSetup);
    } else {
      setModalLoadingOpen(false);
      postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_POLICY_RETRIEVAL_ERROR, '', successfulSetup);
      navigate('/error-banner', { state: { claimNumber } });
    }
  };

  const initiateClaimsByPolicyCall = async (customerInfo: any, urlKey: string, sessionId: string) => {
    setModalLoadingOpen(true);

    console.log(`initiateClaimsByPolicyCall [sessionId: ${sessionId}]`);
    const claimsResponse = await getClaims(urlKey, sessionId, _get(customerInfo, 'updatedAuthToken', ''));

    setModalLoadingOpen(false);

    // Set on every completed call, so a healthy refetch clears a banner left from an earlier one.
    const claimsIssue = hasClaimsRetrievalIssue(claimsResponse);
    dispatch(setClaimsRetrievalIssue(claimsIssue));

    if (claimsIssue) {
      DatadogErrorTracker.trackClaimsRetrievalIssue(claimsResponse);
    }

    // A rejected fetch resolves to an Error with no error property, so check for the data array.
    if (claimsResponse && !claimsResponse.error && Array.isArray(claimsResponse.data)) {
      // Wrap the claims array in response object for Redux action
      dispatch(updateClaimsState({ response: claimsResponse.data }));
    } else {
      postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_ALL_CLAIMS_RETRIEVAL_ERROR, '', claimsResponse);
    }

    return claimsResponse;
  };

  const navigateToNextScreen = (customerInfo: any, claimType: GenericProductType) => {
    setModalLoadingOpen(false);

    const subContext = customerInfo.callingSubContext;

    if (subContext === CallingSubcontexts.CLAIM_DETAIL) {
      navigate('/claim-detail');
    } else if (subContext === 'post-fnol') {
      const route = claimType === GenericProductType.Auto ? '/auto-claim-submission' : '/property-claim-submission';
      navigate(route);
    } else {
      navigate('/error-banner', { state: { claimNumber } });
    }
  };

  // Render content with theme
  console.debug('Rendering with derivedOpcoId:', derivedOpcoId, 'featureFlags:', getFeatureFlagData);

  // Determine which theme to use - always provide a theme to prevent undefined errors
  let activatedTheme = afiChameleonFinalTheme; // Default fallback theme

  if (derivedOpcoId && getFeatureFlagData) {
    /*
    In this scenario, the getFeatureFlagData object is passed directly instead of relying on state,
    since state updates are asynchronous and may not be available for use during execution.
    */
    const loadedTheme = loadThemeByOpCoId(derivedOpcoId, getFeatureFlagData);

    if (!loadedTheme) {
      console.error('Failed to load theme for opcoId:', derivedOpcoId, 'with flags:', getFeatureFlagData);
      console.log('Using default amfamTheme as fallback');
    } else {
      console.log('Theme loaded successfully:', loadedTheme);
      activatedTheme = loadedTheme;
    }
  } else {
    console.log('Using default amfamTheme (waiting for opcoId or feature flags)');
  }

  // Always render with a valid theme
  bodyContent = (
    <ThemeProvider theme={activatedTheme}>
      {modalLoadingOpen ? (
        <ModalLinearActivityIndicator />
      ) : (
        <Routes>
          <Route
            path="/claims-overview"
            element={
              <ScrollMessageWrapper>
                <ClaimsOverview />
              </ScrollMessageWrapper>
            }
          ></Route>

          <Route
            path="/risk-selection"
            element={
              <ScrollMessageWrapper>
                <RiskSelection />
              </ScrollMessageWrapper>
            }
          ></Route>
          <Route
            path="/driver-selection"
            element={
              <ScrollMessageWrapper>
                <DriverSelection />
              </ScrollMessageWrapper>
            }
          ></Route>

          <Route
            path="/time-location-selection"
            element={
              <ScrollMessageWrapper>
                <TimeLocationSelection />
              </ScrollMessageWrapper>
            }
          ></Route>

          <Route
            path="/reporter-info"
            element={
              <ScrollMessageWrapper>
                <ReporterInfo />
              </ScrollMessageWrapper>
            }
          ></Route>
          <Route
            path="/confirm-insureds"
            element={
              <ScrollMessageWrapper>
                <ConfirmInsureds />
              </ScrollMessageWrapper>
            }
          ></Route>

          <Route
            path="/duplicate-check"
            element={
              <ScrollMessageWrapper>
                <ExistingClaims />
              </ScrollMessageWrapper>
            }
          ></Route>

          <Route
            path="/dynamic-question"
            element={
              <ScrollMessageWrapper>
                <DynamicQuestion />
              </ScrollMessageWrapper>
            }
          ></Route>

          <Route
            path="/ers-call-us"
            element={
              <ScrollMessageWrapper>
                <ERSCallUs />
              </ScrollMessageWrapper>
            }
          ></Route>

          <Route
            path="/glass-call-us"
            element={
              <ScrollMessageWrapper>
                <GlassCallUs />
              </ScrollMessageWrapper>
            }
          ></Route>

          <Route
            path="/error-banner"
            element={
              <ScrollMessageWrapper>
                <ErrorBanner />
              </ScrollMessageWrapper>
            }
          ></Route>
          <Route
            path="/property-claim-submission"
            element={
              <ScrollMessageWrapper>
                <PropertyConfirmation />
              </ScrollMessageWrapper>
            }
          ></Route>
          <Route
            path="/auto-claim-submission"
            element={
              <ScrollMessageWrapper>
                <AutoConfirmation />
              </ScrollMessageWrapper>
            }
          ></Route>
          <Route
            path="/timeout-claim-submission"
            element={
              <ScrollMessageWrapper>
                <TimeoutConfirmation />
              </ScrollMessageWrapper>
            }
          ></Route>

          <Route
            path="/claim-detail"
            element={
              <ScrollMessageWrapper>
                <ClaimDetailDetails />
              </ScrollMessageWrapper>
            }
          ></Route>
          <Route
            path="/vehicles"
            element={
              <ScrollMessageWrapper>
                <Vehicles />
              </ScrollMessageWrapper>
            }
          ></Route>
          <Route
            path="/adjuster-contact"
            element={
              <ScrollMessageWrapper>
                <AdjusterContact />
              </ScrollMessageWrapper>
            }
          ></Route>
          <Route
            path="/accordions"
            element={
              <ScrollMessageWrapper>
                <Accordion title="Policy Information" component={<EmergencyServicesHome></EmergencyServicesHome>} />
              </ScrollMessageWrapper>
            }
          ></Route>

          <Route
            path="/repair-home"
            element={
              <ScrollMessageWrapper>
                <RepairServicesHome />
              </ScrollMessageWrapper>
            }
          ></Route>
          <Route
            path="/repair-selection"
            element={
              <ScrollMessageWrapper>
                <RepairServiceSelection />
              </ScrollMessageWrapper>
            }
          ></Route>
          <Route
            path="/photos-home"
            element={
              <ScrollMessageWrapper>
                <PhotoServicesHome />
              </ScrollMessageWrapper>
            }
          ></Route>
          <Route
            path="/photos-selection"
            element={
              <ScrollMessageWrapper>
                <PhotoServicesSelection />
              </ScrollMessageWrapper>
            }
          ></Route>

          <Route
            path="/photos-confirmation"
            element={
              <ScrollMessageWrapper>
                <PhotoServicesConfirmation />
              </ScrollMessageWrapper>
            }
          ></Route>

          <Route
            path="/rental-home"
            element={
              <ScrollMessageWrapper>
                <RentalServicesHome hasRentalCoverage={true} isRentalEligible={true} rentalLimits={'$30 per day'} />
              </ScrollMessageWrapper>
            }
          ></Route>
          <Route
            path="/rental-selection"
            element={
              <ScrollMessageWrapper>
                <RentalServiceSelection />
              </ScrollMessageWrapper>
            }
          ></Route>

          <Route
            path="/rental-finalize"
            element={
              <ScrollMessageWrapper>
                <RentalServicesFinalize />
              </ScrollMessageWrapper>
            }
          ></Route>

          <Route
            path="/emergency-services-home"
            element={
              <ScrollMessageWrapper>
                <EmergencyServicesHome />
              </ScrollMessageWrapper>
            }
          ></Route>

          <Route
            path="/emergency-water-removal-home"
            element={
              <ScrollMessageWrapper>
                <WaterMitigationServiceHome />
              </ScrollMessageWrapper>
            }
          ></Route>
          <Route
            path="/emergency-services-selection"
            element={
              <ScrollMessageWrapper>
                <ServicesSelection />
              </ScrollMessageWrapper>
            }
          ></Route>
          <Route
            path="/auto-service-confirmation"
            element={
              <ScrollMessageWrapper>
                <AutoServiceConfirmation />
              </ScrollMessageWrapper>
            }
          ></Route>

          <Route
            path="/single-service-confirmation"
            element={
              <ScrollMessageWrapper>
                <SingleServiceConfirmation />
              </ScrollMessageWrapper>
            }
          ></Route>
          <Route
            path="/document-damage-services-home"
            element={
              <ScrollMessageWrapper>
                <DocumentDamageServicesHome />
              </ScrollMessageWrapper>
            }
          ></Route>
          <Route
            path="/document-damage-services-selection"
            element={
              <ScrollMessageWrapper>
                <DocumentDamageServiceSelection />
              </ScrollMessageWrapper>
            }
          ></Route>
          <Route
            path="/final-services-confirmation"
            element={
              <ScrollMessageWrapper>
                <FinalServicesConfirmation />
              </ScrollMessageWrapper>
            }
          ></Route>

          <Route
            element={
              <ScrollMessageWrapper>
                <DuplicateClaimConfirmation />
              </ScrollMessageWrapper>
            }
          ></Route>

          <Route
            path="/no-valid-policies-found"
            element={
              <ScrollMessageWrapper>
                <NoValidPoliciesFound />
              </ScrollMessageWrapper>
            }
          ></Route>

          <Route
            path="/third-party-not-found"
            element={
              <ScrollMessageWrapper>
                <PolicyNotFoundConfirmation />
              </ScrollMessageWrapper>
            }
          ></Route>

          <Route
            path="/third-party-confirmation"
            element={
              <ScrollMessageWrapper>
                <ThirdPartyConfirmation />
              </ScrollMessageWrapper>
            }
          ></Route>
        </Routes>
      )}
    </ThemeProvider>
  );

  return bodyContent;
}

export default App;
